import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  StrategyAutomationsActiveBadge,
  StrategyAutomationsWrapper,
} from './style';
import {
  Button,
  CloseMdIcon,
  DotSpinner,
  IconButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { colors } from 'crono-fe-common/theme';
import IconStarsInsights from 'crono-fe-common/icons/Icon-Stars-Insights';
import InfoIcon from 'crono-fe-common/icons/Info-Icon';
import { cn } from 'utils/style-utils';
import CronoCheckbox from 'crono-fe-common/components/CronoCheckbox';
import { InsightsInformationType } from 'crono-fe-common/types/enums/insightsInformationType';
import useGetStrategySettings from 'hooks/services/strategySettings/useGetStrategySettings';
import usePatchStrategySettings from 'hooks/services/strategySettings/usePatchStrategySettings';
import { useConditionalSnackBar } from 'context/snackbar';
import { getError, isRewriteOnInsertEnabled } from 'crono-fe-common/utils';
import EmailIcon from 'crono-fe-common/icons/Icon-Email';
import CallIcon from 'crono-fe-common/icons/Icon-Call';
import LinkedinIcon from 'crono-fe-common/icons/Icon-Linkedin';
import { useAnalytics } from 'context/analytics';
import IconTopArrow from 'crono-fe-common/icons/Icon-Top-Arrow';
import IconBottomArrow from 'crono-fe-common/icons/Icon-Bottom-Arrow';
import ContactDataValidationIcon from 'crono-fe-common/icons/Icon-Contact-Data-Validation';
import {
  CancelButton,
  MainButton,
  RewriteButton,
} from 'crono-fe-common/components/CronoButton';
import IconRewriteCredits from 'crono-fe-common/icons/Icon-Rewrite-Credits';
import { Constants } from 'crono-fe-common/constants/constants';
import { CommunicationStyle } from 'crono-fe-common/types/enums/communicationStyle';
import EditPencilIcon from 'crono-fe-common/icons/Icon-Edit-pencil';
import useGetUserCredits from 'hooks/services/user/useGetUserCredits';

import useGetStrategy, {
  useInvalidateGetStrategy,
} from 'hooks/services/strategy/useGetStrategy';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { TaskTodoSubtype } from 'crono-fe-common/types/enums/taskTodoSubtype';
import { SwitchToTask } from 'crono-fe-common/types/enums/switchToConditionalStep';
import { BlurBackgroundDivFixed } from 'crono-fe-common/components/Layout/BlurBackgroundDiv';
import { createPortal } from 'react-dom';
import AiAssistantFormModal from 'pages/strategy/createStrategyWithAI/AiAssistantForm/aiAssistantFormModal';
import useGenerateTemplatesForStrategy from 'hooks/services/assistant/useGenerateTemplatesForStrategy';
import { CronoMenuItem, CronoSelect } from 'crono-fe-common/components/Select';
import { CheckOnInsertType } from 'crono-fe-common/types/enums/checkOnInsertType';
import { useConfirmModal } from 'context/confirmModal';
import StepsForRewriteSelect from './stepsForRewriteSelect';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import { NotEnoughCreditsBadge } from 'components/NotEnoughCreditsBadge';
import InformationBadge from 'components/InformationBadge';
import DragIcon from 'crono-fe-common/icons/DragIcon';
import { mapLanguageToLabel } from 'utils/formatUtils';

const INFORMATION_BLOCK_TITLES = {
  [InsightsInformationType.GENERAL_INFO]: 'General info',
  [InsightsInformationType.EXPERIENCE]: 'Experience',
  [InsightsInformationType.SOCIAL_PRESENCE]: 'Social presence',
  [InsightsInformationType.KEY_TOPICS]: 'Key topics',
  [InsightsInformationType.CURRENT_COMPANY]: 'Current company',
  [InsightsInformationType.JOB_OPENINGS]: 'Job openings',
  [InsightsInformationType.NEWS]: 'News',
  [InsightsInformationType.FUNDING_ROUNDS]: 'Funding rounds',
};

const INFORMATION_BLOCK_TEXTS = {
  [InsightsInformationType.GENERAL_INFO]: 'Info about contact',
  [InsightsInformationType.EXPERIENCE]: 'Working career, companies',
  [InsightsInformationType.SOCIAL_PRESENCE]: 'Last posts, comments, events',
  [InsightsInformationType.KEY_TOPICS]: 'Main areas of interest',
  [InsightsInformationType.CURRENT_COMPANY]: 'Info about current company',
  [InsightsInformationType.JOB_OPENINGS]: 'Company hiring trend',
  [InsightsInformationType.NEWS]: 'Company latest posts, comments, events',
  [InsightsInformationType.FUNDING_ROUNDS]: 'Company funding rounds',
};

export interface IStrategyAutomationsProps {
  strategyId: number;
  hideTemplateAssistant?: boolean;
  disableEdit?: boolean;
  isDraggable?: boolean;
  onClose?: () => void;
}

const StrategyAutomations = ({
  strategyId,
  hideTemplateAssistant,
  disableEdit,
  onClose,
  isDraggable = false,
}: IStrategyAutomationsProps) => {
  const { data: strategySettingsResponse } = useGetStrategySettings(strategyId);
  const { data: strategyResponse } = useGetStrategy(strategyId);
  const strategy = strategyResponse?.data?.data;
  const analytics = useAnalytics();
  const strategySettings = useMemo(() => {
    return strategySettingsResponse?.data?.data;
  }, [strategySettingsResponse]);

  const { mutate: patchStrategySettings, error: patchStrategySettingsError } =
    usePatchStrategySettings();

  // Template assistant
  //#region
  const {
    mutate: generateTemplatesForStrategy,
    error: errorGenerateTemplatesForStrategy,
    isLoading: isGenerateTemplatesForStrategyLoading,
    isSuccess: isGenerateTemplatesForStrategySuccess,
  } = useGenerateTemplatesForStrategy();

  const invalidateGetStrategy = useInvalidateGetStrategy();

  useEffect(() => {
    if (isGenerateTemplatesForStrategySuccess) {
      invalidateGetStrategy({ strategyId });
    }
  }, [isGenerateTemplatesForStrategySuccess]);

  const { data: userCredits } = useGetUserCredits();
  const assistantCreditsLeft = userCredits?.data?.data?.aiLeft ?? 1;

  const strategyHasOnlyCallsAndVoicenotes = useMemo(() => {
    if (!strategy) return false;
    return strategy.steps.every(
      (s) =>
        s.type === TaskTodoType.CALL ||
        (s.type === TaskTodoType.LINKEDIN &&
          s.subtype === TaskTodoSubtype.LinkedinVoiceNote),
    );
  }, [strategy]);

  const numberOfTemplatesToManipulate = useMemo(() => {
    if (!strategy) return 0;
    //We have to create one template for each step that is not call or voiceNote and for
    //each condition of type inMail or Email (we may have it only inside all strategies)
    return (
      strategy.steps.filter(
        (step) =>
          step.type !== TaskTodoType.CALL &&
          !(
            step.type === TaskTodoType.LINKEDIN &&
            step.subtype === TaskTodoSubtype.LinkedinVoiceNote
          ),
      ).length +
      strategy.steps.filter(
        (step) =>
          step.strategyStepConditional?.type === SwitchToTask.IN_MAIL ||
          step.strategyStepConditional?.type === SwitchToTask.TO_EMAIL,
      ).length
    );
  }, [strategy]);
  const enoughCredits: boolean = assistantCreditsLeft > 0;

  const isThereAnyTemplateAlready = useMemo(() => {
    return strategy?.steps.some((step) => {
      if (step.templateId) return true;
      if (
        step.strategyStepTemplates?.length &&
        step.strategyStepTemplates.length > 0
      )
        return true;
      if (step.strategyStepConditional?.templateId) return true;
    });
  }, [strategy]);

  const [showAiTemplateAssistantModal, setShowAiTemplateAssistantModal] =
    useState<boolean>(false);

  const handleSetShowAiTemplateAssistantModal = useCallback(
    (value: boolean) => {
      //IMPORTANT: IF WE EVER ALLOW TO EDIT FROM THE MODAL, THE POPUP TO EDIT AI VARIABLE HAS AN ISSUE RELATED TO THE SELECTS
      //TAKE THAT INTO CONSIDERATION
      if (disableEdit) return;
      setShowAiTemplateAssistantModal(value);
    },
    [setShowAiTemplateAssistantModal, disableEdit],
  );

  const [aiAssistantData, setAiAssistantData] = useState<{
    productDescription: string;
    targetAudience: string;
    targetAudienceChallenges: string[] | null;
    productBenefits: string[] | null;
    language: string;
    toneOfVoice: CommunicationStyle;
  } | null>(null);

  const resetAiAssistantData = useCallback(() => {
    if (!strategySettings) return;
    setAiAssistantData({
      productDescription: strategySettings.productDescription,
      targetAudience: strategySettings.targetAudience,
      targetAudienceChallenges: strategySettings.targetAudienceChallenges,
      productBenefits: strategySettings.productBenefits,
      language: strategySettings.language ?? '',
      toneOfVoice: strategySettings.style ?? '',
    });
  }, [strategySettings]);

  const areAllAiDataPresent = useMemo(() => {
    return (
      strategySettings?.productDescription &&
      strategySettings?.targetAudience &&
      strategySettings?.targetAudienceChallenges &&
      strategySettings.targetAudienceChallenges.length > 0 &&
      strategySettings?.productBenefits &&
      strategySettings.productBenefits.length > 0 &&
      strategySettings?.language &&
      strategySettings?.style
    );
  }, [strategySettings]);

  useEffect(() => {
    resetAiAssistantData();
  }, [strategySettings]);

  const [areAiAssistantDataChanging, setAreAiAssistantDataChanging] =
    useState(false);

  const handleCancelChangesAreAiData = useCallback(() => {
    setAreAiAssistantDataChanging(false);
    resetAiAssistantData();
  }, [strategySettings, setAreAiAssistantDataChanging, resetAiAssistantData]);

  const handleSaveChangesAreAiData = useCallback(() => {
    if (!aiAssistantData) return;
    const { toneOfVoice, ...rest } = aiAssistantData;
    patchStrategySettings({
      strategyId,
      ...rest,
      style: toneOfVoice,
    });
    setAreAiAssistantDataChanging(false);
  }, [aiAssistantData]);

  const { openModal } = useConfirmModal();

  const handleCompleteGenerateTemplatesWithAi = useCallback(() => {
    if (analytics) {
      analytics.track('generate-templates-of-strategy-with-AI', {});
    }
    generateTemplatesForStrategy({ strategyId });
  }, [analytics, strategyId, generateTemplatesForStrategy]);

  const handleGenerateTemplatesWithAi = useCallback(() => {
    if (isThereAnyTemplateAlready) {
      openModal({
        title: 'Do you want to replace existing templates?',
        text: 'This sequence is already using some templates. If you continue all Email and LinkedIn messages will be replaced by AI.',
        type: 'info',
        cancelText: 'Cancel',
        confirmFunction: () => {
          handleCompleteGenerateTemplatesWithAi();
        },
        confirmText: 'Continue',
      });
    } else {
      handleCompleteGenerateTemplatesWithAi();
    }
  }, [handleCompleteGenerateTemplatesWithAi, isThereAnyTemplateAlready]);

  const templateAssistantOptionsJSX = useMemo(() => {
    const isGenerateButtonDisabled: boolean =
      !enoughCredits ||
      strategyHasOnlyCallsAndVoicenotes ||
      !areAllAiDataPresent;

    const disabledTooltipMessage = strategyHasOnlyCallsAndVoicenotes
      ? 'Rewrite with AI can be applied to emails and LinkedIn messages only'
      : 'Click "Edit" to complete the setup of the AI assistant';

    const rewriteButtonEl = (
      <div>
        <RewriteButton
          onClick={handleGenerateTemplatesWithAi}
          disabled={isGenerateButtonDisabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 8px 8px 12px',
            height: 40,
            width: 'fit-content',
          }}
        >
          <span>
            <IconStarsInsights
              color={isGenerateButtonDisabled ? colors.grey11 : colors.mainDark}
              viewBox="0 0 16 16"
            />
          </span>
          <p
            className={cn(
              'text-sm font-medium',
              isGenerateButtonDisabled ? 'text-neutral-500' : 'text-[#08193B]',
            )}
          >
            Write
          </p>
        </RewriteButton>
      </div>
    );

    return (
      <>
        {showAiTemplateAssistantModal &&
          aiAssistantData &&
          createPortal(
            <BlurBackgroundDivFixed
              style={{
                zIndex: '36000 !important',
              }}
            >
              <AiAssistantFormModal
                close={() => {
                  handleSetShowAiTemplateAssistantModal(false);
                  handleCancelChangesAreAiData();
                }}
                save={() => {
                  handleSetShowAiTemplateAssistantModal(false);
                  patchStrategySettings({
                    strategyId,
                    ...aiAssistantData,
                    language: aiAssistantData.language,
                    style: aiAssistantData.toneOfVoice,
                  });
                }}
                productDescription={aiAssistantData.productDescription}
                audience={aiAssistantData.targetAudience}
                challenges={aiAssistantData.targetAudienceChallenges ?? []}
                hows={aiAssistantData.productBenefits ?? []}
                language={aiAssistantData.language}
                toneOfVoice={aiAssistantData.toneOfVoice}
                setAudience={(audience) =>
                  setAiAssistantData((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      targetAudience: audience,
                    };
                  })
                }
                setChallenges={(challenges) =>
                  setAiAssistantData((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      targetAudienceChallenges: challenges,
                    };
                  })
                }
                setHows={(hows) =>
                  setAiAssistantData((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      productBenefits: hows,
                    };
                  })
                }
                setLanguage={(language) =>
                  setAiAssistantData((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      language,
                    };
                  })
                }
                setToneOfVoice={(toneOfVoice) =>
                  setAiAssistantData((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      toneOfVoice,
                    };
                  })
                }
                setProductDescription={(productDescription) =>
                  setAiAssistantData((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      productDescription,
                    };
                  })
                }
              />
            </BlurBackgroundDivFixed>,
            document.body,
          )}
        <div className="template-assistant-box">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Product info</p>
          </div>
          <div className="box-content-product-info">
            We help{' '}
            {strategySettings?.targetAudience ? (
              <span className="variable-value">
                {strategySettings.targetAudience}
              </span>
            ) : (
              <span
                className="empty-box"
                onClick={() => handleSetShowAiTemplateAssistantModal(true)}
              >
                Head of sales
              </span>
            )}{' '}
            overcome challenges like{' '}
            {strategySettings?.targetAudienceChallenges ? (
              <span className="variable-value">
                {strategySettings.targetAudienceChallenges.join('; ')}
              </span>
            ) : (
              <span
                className="empty-box"
                onClick={() => handleSetShowAiTemplateAssistantModal(true)}
              >
                Industry challenges
              </span>
            )}{' '}
            with{' '}
            {strategySettings?.productBenefits ? (
              <span className="variable-value">
                {strategySettings.productBenefits.join('; ')}
              </span>
            ) : (
              <span
                className="empty-box"
                onClick={() => handleSetShowAiTemplateAssistantModal(true)}
              >
                Product benefits
              </span>
            )}
          </div>
        </div>
        <div className="template-assistant-box">
          <p className="text-sm font-medium">Language</p>
          <div
            className="fake-select"
            onClick={() => handleSetShowAiTemplateAssistantModal(true)}
          >
            <p className="text-sm">
              {mapLanguageToLabel(
                strategySettings?.language ?? Constants.defaultLanguage,
              )}
            </p>
          </div>
        </div>
        <div className="template-assistant-box">
          <p className="text-sm font-medium">Tone of Voice</p>
          <div
            className="fake-select"
            onClick={() => handleSetShowAiTemplateAssistantModal(true)}
          >
            <p className="text-sm">
              {strategySettings?.style ?? CommunicationStyle.FRIENDLY}
            </p>
          </div>
          {/*
          Left here in the case we want to restore the Select
          <CronoSelect
            componentsProps={{
              input: {
                style: {
                  height: 32,
                  padding: 0,
                },
              },
            }}
            MenuProps={{
              style: {
                zIndex: 35100,
              },
            }}
            sx={{
              height: 40,
              borderRadius: '8px',
            }}
            disabled={disableEdit}
            value={aiAssistantData?.toneOfVoice}
            onChange={(e) => {
              setAreAiAssistantDataChanging(true);
              setAiAssistantData((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  toneOfVoice: e.target.value as CommunicationStyle,
                };
              });
            }}
            displayEmpty
            renderValue={(value) => {
              return value ? (value as string) : 'Not selected';
            }}
          >
            {CommunicationStyleValues.map((toneOfVoice) => {
              return (
                <CronoMenuItem value={toneOfVoice} key={toneOfVoice}>
                  {toneOfVoice}
                </CronoMenuItem>
              );
            })}
          </CronoSelect> */}
        </div>

        <NotEnoughCreditsBadge
          usagePlace="ai automations"
          notEnoughAICredits={!enoughCredits}
        />

        <div className="w-full flex items-center justify-center">
          {isGenerateTemplatesForStrategyLoading ? (
            <div>
              <DotSpinner />
            </div>
          ) : (
            !disableEdit &&
            (isGenerateButtonDisabled ? (
              <Tooltip delayDuration={400}>
                <TooltipTrigger asChild>{rewriteButtonEl}</TooltipTrigger>
                <TooltipContent>
                  {!enoughCredits
                    ? "You don't have enough credits"
                    : disabledTooltipMessage}
                </TooltipContent>
              </Tooltip>
            ) : (
              rewriteButtonEl
            ))
          )}
        </div>
        {areAiAssistantDataChanging && (
          <div className="flex items-center gap-2 w-full">
            <CancelButton
              style={{
                height: 40,
                margin: 0,
              }}
              fullWidth
              onClick={handleCancelChangesAreAiData}
            >
              Cancel
            </CancelButton>
            <MainButton
              style={{
                height: 40,
                margin: 0,
              }}
              fullWidth
              onClick={handleSaveChangesAreAiData}
            >
              Save
            </MainButton>
          </div>
        )}
      </>
    );
  }, [
    strategySettings,
    showAiTemplateAssistantModal,
    enoughCredits,
    handleSetShowAiTemplateAssistantModal,
    aiAssistantData,
    setAiAssistantData,
    numberOfTemplatesToManipulate,
    isGenerateTemplatesForStrategyLoading,
    areAiAssistantDataChanging,
    handleCancelChangesAreAiData,
    handleSaveChangesAreAiData,
    strategyHasOnlyCallsAndVoicenotes,
    handleGenerateTemplatesWithAi,
    disableEdit,
  ]);

  //#endregion

  // Rewrite in bulk
  //#region
  const [rewriteAutomationEnabled, setRewriteAutomationEnabled] =
    useState(false);
  useEffect(() => {
    const newValue = isRewriteOnInsertEnabled(strategySettings ?? null);
    if (rewriteAutomationEnabled === newValue) return;
    if (analytics) {
      if (newValue) {
        analytics.track('rewrite-automation-enable', {
          strategyId,
        });
      } else {
        analytics.track('rewrite-automation-disable', {
          strategyId,
        });
      }
    }
    setRewriteAutomationEnabled(newValue);
  }, [strategySettings]);

  const [informationToUseForRewrite, setInformationToUseForRewrite] = useState<
    InsightsInformationType[]
  >([]);
  useEffect(() => {
    if (!strategySettings) return;
    setInformationToUseForRewrite(
      strategySettings.selectedInsightsForRewrite ?? [],
    );
  }, [strategySettings?.selectedInsightsForRewrite]);

  const [areInformationToUseChanging, setAreInformationToUseChanging] =
    useState(false);

  const handleCancelChangesAreInformationToUse = useCallback(() => {
    setAreInformationToUseChanging(false);
    setInformationToUseForRewrite(
      strategySettings?.selectedInsightsForRewrite ?? [],
    );
  }, [
    strategySettings,
    setAreInformationToUseChanging,
    setInformationToUseForRewrite,
  ]);

  const toggleInformationToUse = useCallback(
    (informationType: InsightsInformationType) => {
      const newInformationToUseForRewrite = informationToUseForRewrite.includes(
        informationType,
      )
        ? informationToUseForRewrite.filter((type) => type !== informationType)
        : [...informationToUseForRewrite, informationType];
      setAreInformationToUseChanging(true);
      setInformationToUseForRewrite(newInformationToUseForRewrite);
    },
    [informationToUseForRewrite, setInformationToUseForRewrite],
  );

  const handleSaveInformationToUse = useCallback(() => {
    patchStrategySettings({
      strategyId,
      selectedInsightsForRewrite: informationToUseForRewrite,
    });
    setAreInformationToUseChanging(false);
  }, [informationToUseForRewrite]);

  const rewriteOptionsJSX = useMemo(() => {
    const renderInformationCheckBox = (
      informationType: InsightsInformationType,
    ) => {
      return (
        <CronoCheckbox
          checked={informationToUseForRewrite.includes(informationType)}
          onClick={() => {
            if (disableEdit) return;
            toggleInformationToUse(informationType);
          }}
          disabled={disableEdit}
        />
      );
    };

    return Object.values(InsightsInformationType).map((type) => {
      return (
        <div
          key={type}
          className={cn(`information-block ${type}`, {
            selected: informationToUseForRewrite.includes(type),
          })}
        >
          <div className={'rewrite-checkbox'}>
            {renderInformationCheckBox(type)}
          </div>
          <div className={'text-wrapper'}>
            <p className="text-xs font-medium text-gray-900">
              {INFORMATION_BLOCK_TITLES[type]}
            </p>
            <p className="text-xs text-gray-700">
              {INFORMATION_BLOCK_TEXTS[type]}
            </p>
          </div>
        </div>
      );
    });
  }, [informationToUseForRewrite, toggleInformationToUse, disableEdit]);
  //#endregion

  // Contacts validation
  //#region
  const [contactsValidationEnabled, setContactsValidationEnabled] =
    useState(false);
  useEffect(() => {
    if (!strategySettings) return;

    const newValue =
      strategySettings.checkEmailOnInsert ||
      strategySettings.checkLinkedinOnInsert ||
      strategySettings.checkPhoneOnInsert;
    if (contactsValidationEnabled === newValue) return;
    if (analytics) {
      if (newValue) {
        analytics.track('contacts-validation-automation-enable', {
          strategyId,
        });
      } else {
        analytics.track('contacts-validation-automation-disable', {
          strategyId,
        });
      }
    }
    setContactsValidationEnabled(newValue);
  }, [strategySettings]);

  const [contactsDataValidationData, setContactsDataValidationData] = useState<{
    checkEmailOnInsert: boolean;
    checkPhoneOnInsert: boolean;
    checkLinkedinOnInsert: boolean;
    checkOnInsertType: CheckOnInsertType;
  }>({
    checkEmailOnInsert: false,
    checkPhoneOnInsert: false,
    checkLinkedinOnInsert: false,
    checkOnInsertType: CheckOnInsertType.DO_NOT_ENROLL,
  });

  useEffect(() => {
    if (!strategySettings) return;
    setContactsDataValidationData({
      checkEmailOnInsert: strategySettings.checkEmailOnInsert,
      checkPhoneOnInsert: strategySettings.checkPhoneOnInsert,
      checkLinkedinOnInsert: strategySettings.checkLinkedinOnInsert,
      checkOnInsertType: strategySettings.checkOnInsertType,
    });
  }, [
    strategySettings?.checkEmailOnInsert,
    strategySettings?.checkPhoneOnInsert,
    strategySettings?.checkLinkedinOnInsert,
    strategySettings?.checkOnInsertType,
  ]);

  const [
    areContactsDataValidationChanging,
    setAreContactsDataValidationChanging,
  ] = useState(false);

  const handleCancelChangesContactsDataValidation = useCallback(() => {
    setAreContactsDataValidationChanging(false);
    setContactsDataValidationData({
      checkEmailOnInsert: strategySettings?.checkEmailOnInsert ?? false,
      checkLinkedinOnInsert: strategySettings?.checkLinkedinOnInsert ?? false,
      checkPhoneOnInsert: strategySettings?.checkPhoneOnInsert ?? false,
      checkOnInsertType:
        strategySettings?.checkOnInsertType ?? CheckOnInsertType.DO_NOT_ENROLL,
    });
  }, [
    strategySettings,
    setAreContactsDataValidationChanging,
    setContactsDataValidationData,
  ]);

  const toggleCheckEmailOnInsert = () => {
    setAreContactsDataValidationChanging(true);
    setContactsDataValidationData((prev) => {
      return {
        ...prev,
        checkEmailOnInsert: !prev.checkEmailOnInsert,
      };
    });
  };

  const toggleCheckPhoneOnInsert = () => {
    setAreContactsDataValidationChanging(true);
    setContactsDataValidationData((prev) => {
      return {
        ...prev,
        checkPhoneOnInsert: !prev.checkPhoneOnInsert,
      };
    });
  };

  const toggleCheckLinkedinOnInsert = () => {
    setAreContactsDataValidationChanging(true);
    setContactsDataValidationData((prev) => {
      return {
        ...prev,
        checkLinkedinOnInsert: !prev.checkLinkedinOnInsert,
      };
    });
  };

  const setCheckOnInsertType = (newType: CheckOnInsertType) => {
    setAreContactsDataValidationChanging(true);
    setContactsDataValidationData((prev) => {
      return {
        ...prev,
        checkOnInsertType: newType,
      };
    });
  };

  const handleSaveContactsDataValidation = useCallback(() => {
    patchStrategySettings({
      strategyId,
      checkEmailOnInsert: contactsDataValidationData.checkEmailOnInsert,
      checkPhoneOnInsert: contactsDataValidationData.checkPhoneOnInsert,
      checkLinkedinOnInsert: contactsDataValidationData.checkLinkedinOnInsert,
      checkOnInsertType: contactsDataValidationData.checkOnInsertType,
    });
    setAreContactsDataValidationChanging(false);
  }, [contactsDataValidationData]);

  const contactsValidationOptionsJSX = useMemo(() => {
    if (!contactsDataValidationData) return null;

    return (
      <>
        <div
          key={1}
          className={cn(`information-block`, {
            selected: contactsDataValidationData.checkEmailOnInsert,
          })}
        >
          <div className={`validation-icon-wrapper`}>
            <EmailIcon color={colors.black} size={16} />
          </div>
          <p className="text-xs font-medium text-gray-900">
            Missing/Invalid Email
          </p>
          <div className={'validation-checkbox'}>
            <CronoCheckbox
              checked={contactsDataValidationData.checkEmailOnInsert}
              onClick={() => {
                if (disableEdit) return;
                toggleCheckEmailOnInsert();
              }}
              disabled={disableEdit}
            />
          </div>
        </div>
        <div
          key={2}
          className={cn(`information-block`, {
            selected: contactsDataValidationData.checkPhoneOnInsert,
          })}
        >
          <div className={`validation-icon-wrapper`}>
            <CallIcon color={colors.black} size={16} />
          </div>
          <p className="text-xs font-medium text-gray-900">
            Missing/Invalid Phone
          </p>
          <div className={'validation-checkbox'}>
            <CronoCheckbox
              checked={contactsDataValidationData.checkPhoneOnInsert}
              onClick={() => {
                if (disableEdit) return;
                toggleCheckPhoneOnInsert();
              }}
              disabled={disableEdit}
            />
          </div>
        </div>
        <div
          key={3}
          className={cn(`information-block`, {
            selected: contactsDataValidationData.checkLinkedinOnInsert,
          })}
        >
          <div className={`validation-icon-wrapper`}>
            <LinkedinIcon color={colors.black} size={16} />
          </div>
          <p className="text-xs font-medium text-gray-900">
            Missing/Invalid LinkedIn URL
          </p>
          <div className={'validation-checkbox'}>
            <CronoCheckbox
              checked={contactsDataValidationData.checkLinkedinOnInsert}
              onClick={() => {
                if (disableEdit) return;
                toggleCheckLinkedinOnInsert();
              }}
              disabled={disableEdit}
            />
          </div>
        </div>
      </>
    );
  }, [contactsDataValidationData]);
  //#endregion

  const [openedAccordion, setOpenedAccordion] = useState<
    'templateAssistant' | 'rewriteAutomation' | 'contactDataValidation' | null
  >(null);
  const rewriteAutomationOpened = openedAccordion === 'rewriteAutomation';
  const contactDataValidationOpened =
    openedAccordion === 'contactDataValidation';
  const templateAssistantOpened = openedAccordion === 'templateAssistant';

  const isTemplateAssistantDisabled =
    areInformationToUseChanging || areContactsDataValidationChanging;
  const isContactDataValidationDisabled =
    areInformationToUseChanging || areAiAssistantDataChanging;
  const isRewriteTemplatesWithAIDisabled =
    areContactsDataValidationChanging || areAiAssistantDataChanging;

  useConditionalSnackBar([
    {
      condition: !!patchStrategySettingsError,
      message:
        getError(patchStrategySettingsError) ??
        'Error during saving changes to sequence automations',
      severity: 'error',
    },
    {
      condition: !!errorGenerateTemplatesForStrategy,
      message:
        getError(errorGenerateTemplatesForStrategy) ??
        'Error while generating templates for the sequence',
      severity: 'error',
    },
    {
      condition: isGenerateTemplatesForStrategySuccess,
      message: 'Templates for the sequence has been successfully generated',
      severity: 'success',
    },
  ]);

  return (
    <StrategyAutomationsWrapper>
      <div className="flex items-center p-4 gap-2 max-h-14">
        {onClose && (
          <IconButton
            variant="light"
            tone="lightGray"
            shape="rounded"
            label="Close automations"
            size="sm"
            icon={CloseMdIcon}
            onClick={onClose}
          />
        )}

        <p className="text-lg font-bold flex-1 text-center">Automations</p>

        {isDraggable && (
          <button
            className="handle cursor-grab active:cursor-grabbing bg-transparent border-none p-0 hover:bg-neutral-100 rounded-md h-fit grid place-content-center"
            title="Drag to move"
          >
            <DragIcon />
          </button>
        )}
      </div>

      <div className="h-px bg-neutral-300" />

      <div className={'automations-options-container'}>
        {!hideTemplateAssistant && (
          <div className={'automations-container'}>
            <div className="automation-box">
              <div
                className={cn('automation-box-header', {
                  'flat-bottom': templateAssistantOpened,
                  disabled: isTemplateAssistantDisabled,
                  active: strategy?.rewrittenWithAi,
                })}
                style={{
                  cursor: !templateAssistantOpened ? 'pointer' : undefined,
                }}
                onClick={() => {
                  if (
                    !templateAssistantOpened &&
                    !isTemplateAssistantDisabled
                  ) {
                    setOpenedAccordion('templateAssistant');
                  }
                }}
              >
                <IconRewriteCredits
                  className="icon-verification"
                  color={colors.black}
                />
                <div className="flex flex-col flex-1 h-full">
                  <div className="flex gap-2">
                    <p className="text-sm font-medium text-gray-900 pb-1">
                      <span className="pr-0.5">Write templates</span>

                      <span className="whitespace-nowrap inline-flex items-center">
                        with AI
                        <span
                          className="icon-in-text-wrapper"
                          style={{ bottom: 0 }}
                        >
                          <InformationBadge
                            title={
                              'Use Crono’s AI model to draft all your strategy’s templates in seconds providing your preferred information.'
                            }
                          />
                        </span>
                      </span>
                    </p>
                  </div>
                  {!templateAssistantOpened && (
                    <p className="text-xs text-gray-700">
                      Draft all templates with AI in seconds for all contacts
                    </p>
                  )}
                </div>
                <div className="flex items-center">
                  {strategy?.rewrittenWithAi && (
                    <StrategyAutomationsActiveBadge>
                      Active
                    </StrategyAutomationsActiveBadge>
                  )}
                  {templateAssistantOpened ? (
                    <IconTopArrow
                      className={cn('arrow')}
                      onClick={() => {
                        setOpenedAccordion(null);
                      }}
                    />
                  ) : (
                    <IconBottomArrow
                      className={cn('arrow', {
                        disabled: isTemplateAssistantDisabled,
                      })}
                      onClick={() => {
                        if (!isTemplateAssistantDisabled)
                          setOpenedAccordion('templateAssistant');
                      }}
                    />
                  )}
                </div>
              </div>
              {templateAssistantOpened && (
                <div className="automation-box-footer">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">
                        Template settings
                      </p>
                      {!disableEdit && (
                        <div
                          className="edit-product-info-box"
                          onClick={() =>
                            handleSetShowAiTemplateAssistantModal(true)
                          }
                        >
                          <EditPencilIcon color={colors.primary} />
                          <p className="text-sm font-medium">Edit</p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-700">
                      Set settings for sequence templates of this sequence. This
                      info will be applied during rewriting
                    </p>
                  </div>
                  {templateAssistantOptionsJSX}
                </div>
              )}
            </div>
          </div>
        )}
        <div className={'automations-container'}>
          <div className="automation-box">
            <div
              className={cn('automation-box-header', {
                'flat-bottom': rewriteAutomationOpened,
                disabled: isRewriteTemplatesWithAIDisabled,
                active: rewriteAutomationEnabled,
              })}
              style={{
                cursor: !rewriteAutomationOpened ? 'pointer' : undefined,
              }}
              onClick={() => {
                if (
                  !rewriteAutomationOpened &&
                  !isRewriteTemplatesWithAIDisabled
                ) {
                  setOpenedAccordion('rewriteAutomation');
                }
              }}
            >
              <IconStarsInsights
                color={colors.black}
                size={24}
                className="icon-starts-insights"
              />
              <div className="flex flex-col flex-1 h-full">
                <div className="flex gap-2 items-center">
                  <p className="text-sm font-medium text-gray-900 pb-1 -mb-px">
                    <span className="pr-0.5">Rewrite and personalise</span>

                    <span className="whitespace-nowrap inline-flex items-center">
                      with AI
                      <span
                        className="icon-in-text-wrapper"
                        style={{ bottom: 0 }}
                      >
                        <InformationBadge
                          title={
                            'All sequences will be automatically rewritten while being enrolled into the sequence to make the templates personalised and relevant.'
                          }
                        />
                      </span>
                    </span>
                  </p>
                </div>
                {!rewriteAutomationOpened && (
                  <p className="text-xs text-gray-700">
                    Create relevance for each contact with AI based on social
                    insights
                  </p>
                )}
              </div>
              <div className="flex items-center">
                {rewriteAutomationEnabled && (
                  <StrategyAutomationsActiveBadge>
                    Active
                  </StrategyAutomationsActiveBadge>
                )}
                {rewriteAutomationOpened ? (
                  <IconTopArrow
                    className={cn('arrow', {
                      disabled: areInformationToUseChanging,
                    })}
                    onClick={() => {
                      if (!areInformationToUseChanging)
                        setOpenedAccordion(null);
                    }}
                  />
                ) : (
                  <IconBottomArrow
                    className={cn('arrow', {
                      disabled: isRewriteTemplatesWithAIDisabled,
                    })}
                    onClick={() => {
                      if (!isRewriteTemplatesWithAIDisabled)
                        setOpenedAccordion('rewriteAutomation');
                    }}
                  />
                )}
              </div>
            </div>
            {rewriteAutomationOpened && (
              <div className="automation-box-footer">
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-gray-900">
                    Rewriting scope
                  </p>
                  <p className="text-xs text-gray-700">
                    Choose which steps you want to rewrite. Only steps with
                    templates will be rewritten.
                  </p>

                  <FlexDiv style={{ margin: '8px 0px' }} width={'100%'}>
                    <StepsForRewriteSelect
                      strategy={strategy ?? null}
                      disableEdit={disableEdit}
                    />
                  </FlexDiv>
                </div>

                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-gray-900">
                    Social Insights
                  </p>
                  <p className="text-xs text-gray-700">
                    Choose which LinkedIn information will be used by AI to
                    rewrite templates.
                  </p>
                </div>

                {rewriteOptionsJSX}
                {areInformationToUseChanging && (
                  <div className="flex items-center gap-2 w-full">
                    <CancelButton
                      style={{
                        height: 40,
                        margin: 0,
                      }}
                      fullWidth
                      onClick={handleCancelChangesAreInformationToUse}
                    >
                      Cancel
                    </CancelButton>
                    <MainButton
                      style={{
                        height: 40,
                        margin: 0,
                      }}
                      fullWidth
                      onClick={handleSaveInformationToUse}
                    >
                      Save
                    </MainButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={'automations-container'}>
          <div className="automation-box">
            <div
              className={cn('automation-box-header', {
                'flat-bottom': contactDataValidationOpened,
                disabled: isContactDataValidationDisabled,
                active: contactsValidationEnabled,
              })}
              style={{
                cursor: !contactDataValidationOpened ? 'pointer' : undefined,
              }}
              onClick={() => {
                if (
                  !contactDataValidationOpened &&
                  !isContactDataValidationDisabled
                ) {
                  setOpenedAccordion('contactDataValidation');
                }
              }}
            >
              <ContactDataValidationIcon
                className="icon-verification"
                color={colors.black}
              />
              <div className="flex flex-col flex-1 h-full">
                <div className="flex gap-2 items-center">
                  <p className="text-sm font-medium text-gray-900 pb-1">
                    <span className="pr-0.5">Contact Data</span>

                    <span className="whitespace-nowrap inline-flex items-center">
                      validation
                      <span
                        className="icon-in-text-wrapper"
                        style={{ bottom: 0 }}
                      >
                        <InformationBadge
                          title={
                            'By default contacts with missing/invalid data will be enrolled in the sequence without skipping the tasks.'
                          }
                        />
                      </span>
                    </span>
                  </p>
                </div>
                {!contactDataValidationOpened && (
                  <p className="text-xs text-gray-700">
                    Choose how to enroll contacts with missing/Invalid data
                  </p>
                )}
              </div>
              <div className="flex items-center">
                {contactsValidationEnabled && (
                  <StrategyAutomationsActiveBadge>
                    Active
                  </StrategyAutomationsActiveBadge>
                )}
                {contactDataValidationOpened ? (
                  <IconTopArrow
                    className={cn('arrow', {
                      disabled: areContactsDataValidationChanging,
                    })}
                    onClick={() => {
                      if (!areContactsDataValidationChanging)
                        setOpenedAccordion(null);
                    }}
                  />
                ) : (
                  <IconBottomArrow
                    className={cn('arrow', {
                      disabled: isContactDataValidationDisabled,
                    })}
                    onClick={() => {
                      if (!isContactDataValidationDisabled)
                        setOpenedAccordion('contactDataValidation');
                    }}
                  />
                )}
              </div>
            </div>
            {contactDataValidationOpened && (
              <div className="automation-box-footer">
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-gray-900">
                    Data validation
                  </p>
                  <p className="text-xs text-gray-700">
                    Select which data to verify when the contact is enrolled in
                    this sequence:
                  </p>
                </div>
                {contactsValidationOptionsJSX}
                <div className="flex flex-col gap-2 my-2">
                  <p className="text-sm font-medium text-gray-900">Rule</p>
                  <p className="text-xs text-gray-700 -mt-0.5">
                    Select what to do with the contact based on the data
                    validation:
                  </p>
                  <CronoSelect
                    value={contactsDataValidationData.checkOnInsertType}
                    onChange={(ev) => {
                      setCheckOnInsertType(
                        ev.target.value as CheckOnInsertType,
                      );
                    }}
                    disabled={disableEdit}
                    style={{
                      height: 40,
                      borderRadius: '8px',
                    }}
                  >
                    <CronoMenuItem
                      value={CheckOnInsertType.DO_NOT_ENROLL}
                      key={CheckOnInsertType.DO_NOT_ENROLL}
                    >
                      Do not enroll contacts in the sequence
                    </CronoMenuItem>
                    <CronoMenuItem
                      value={CheckOnInsertType.SKIP_TASKS}
                      key={CheckOnInsertType.SKIP_TASKS}
                    >
                      Enroll contacts but skip the tasks
                    </CronoMenuItem>
                  </CronoSelect>
                  {contactsDataValidationData.checkOnInsertType ===
                    CheckOnInsertType.SKIP_TASKS && (
                    <div className={'info-banner'}>
                      <InfoIcon
                        color={colors.intenseBlue}
                        size={16}
                        style={{ minHeight: '16px', minWidth: '16px' }}
                      />
                      <p className="text-xs font-medium text-sky-700">
                        Once skipped, the task cannot be recovered
                      </p>
                    </div>
                  )}
                </div>
                {areContactsDataValidationChanging && (
                  <div className="flex items-center gap-2 w-full">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handleCancelChangesContactsDataValidation}
                    >
                      Cancel
                    </Button>
                    <MainButton
                      className="flex-1"
                      onClick={handleSaveContactsDataValidation}
                    >
                      Save
                    </MainButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </StrategyAutomationsWrapper>
  );
};

export default StrategyAutomations;
